import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiLoader, FiEye, FiEyeOff, FiCheck, FiArrowRight } from 'react-icons/fi';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import LinkedInLoginButton from '../../components/auth/LinkedInLoginButton';
import { toast } from 'react-toastify';
import { promoApi } from '../../services/api';

const FEATURES = [
  'AI-powered resume analysis & optimization',
  'ATS compatibility scoring for every application',
  'Smart job matching with personalized insights',
  'Track applications & improve over time',
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, googleLogin, linkedinLogin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Capture promo context from the URL once on mount so that whichever
  // login path the user picks (form, Google, or LinkedIn callback) we can
  // claim the lead afterwards.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('from') === 'promo') {
      const leadId = params.get('lead_id');
      try {
        if (leadId) localStorage.setItem('pendingPromoLeadId', leadId);
        localStorage.setItem('redirectAfterLogin', '/resume-diagnosis/report');
      } catch (e) { /* ignore */ }
      const emailFromUrl = params.get('email');
      if (emailFromUrl && !email) setEmail(emailFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    localStorage.removeItem('resumemo_sidebar_tour_completed');
    localStorage.removeItem('resumemo_onboarding_seen');

    const finishRedirect = () => {
      const redirectUrl = localStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl);
      } else {
        navigate('/');
      }
    };

    let pendingLeadId = null;
    try { pendingLeadId = localStorage.getItem('pendingPromoLeadId'); }
    catch (e) { /* ignore */ }

    if (pendingLeadId) {
      promoApi.claim(pendingLeadId)
        .catch(() => {})
        .finally(() => {
          try { localStorage.removeItem('pendingPromoLeadId'); }
          catch (e) { /* ignore */ }
          finishRedirect();
        });
    } else {
      finishRedirect();
    }
  }, [isAuthenticated, navigate]);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const storedState = sessionStorage.getItem('linkedinState');
    
    if (code && state) {
      if (state !== storedState) {
        setError('Invalid state parameter. Please try again.');
        return;
      }
      sessionStorage.removeItem('linkedinState');
      handleLinkedInCallback(code);
    }
  }, [location]);
  
  const handleLinkedInCallback = async (code) => {
    try {
      setLoading(true);
      setError('');
      
      const result = await linkedinLogin(code);
      
      if (result.success) {
        navigate(result.redirectUrl || '/');
      } else if (result.needsRegistration && result.email) {
        navigate('/register', { 
          state: { 
            email: result.email,
            name: result.name || '',
            fromLinkedIn: true
          }
        });
        toast.info('Please complete your registration');
      } else {
        setError('LinkedIn login failed. Please try again.');
      }
    } catch (err) {
      setError('Failed to log in with LinkedIn. Please try again.');
      console.error('LinkedIn login error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      const result = await login(email, password);
      
      if (result.success) {
        navigate(result.redirectUrl || '/');
      }
    } catch (err) {
      setError('Failed to log in. Please check your credentials.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[48%] relative overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary-600/20 blur-3xl animate-float" />
          <div className="absolute top-1/2 -right-32 w-80 h-80 rounded-full bg-primary-500/15 blur-3xl animate-float-delayed" />
          <div className="absolute -bottom-20 left-1/4 w-72 h-72 rounded-full bg-primary-400/10 blur-3xl animate-float-slow" />
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '32px 32px',
            }}
          />
        </div>

        <div className="relative z-10 flex flex-col justify-start items-center gap-y-10 p-12 pt-10 xl:p-16 xl:pt-12 w-full">
          {/* Logo */}
          <div className="self-start">
            <Link to="/" className="flex items-center space-x-3 group">
              <img src="/logo-white.svg" alt="Resumemo" className="h-9 w-auto" />
              <span className="text-xl font-semibold text-white tracking-tight">Resumemo</span>
            </Link>
          </div>

          {/* Main content */}
          <div className="max-w-md w-full text-center">
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Land your dream job with AI-powered resumes
            </h2>
            <p className="text-primary-200 text-base leading-relaxed mb-10">
              Join thousands of professionals who've transformed their job search with intelligent resume optimization.
            </p>
            
            {/* Feature list */}
            <div className="space-y-4">
              {FEATURES.map((feature, i) => (
                <div key={i} className="flex items-start space-x-3 text-left">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500/30 flex items-center justify-center mt-0.5">
                    <FiCheck className="w-3 h-3 text-primary-200" />
                  </div>
                  <span className="text-primary-100 text-sm leading-relaxed">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Social proof */}
          <div className="flex items-center justify-center space-x-8 w-full max-w-md">
            <div>
              <div className="text-2xl font-bold text-white">10K+</div>
              <div className="text-xs text-primary-300 mt-0.5">Active Users</div>
            </div>
            <div className="w-px h-10 bg-primary-600" />
            <div>
              <div className="text-2xl font-bold text-white">50K+</div>
              <div className="text-xs text-primary-300 mt-0.5">Resumes Optimized</div>
            </div>
            <div className="w-px h-10 bg-primary-600" />
            <div>
              <div className="text-2xl font-bold text-white">85%</div>
              <div className="text-xs text-primary-300 mt-0.5">Interview Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Mobile header */}
        <header className="lg:hidden w-full py-4 px-5 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2 group">
              <img src="/logo.svg" alt="Resumemo" className="h-7 w-auto" />
              <span className="text-lg font-semibold text-gray-900 tracking-tight">Resumemo</span>
            </Link>
            <Link to="/register" className="text-base text-primary-600 font-medium hover:text-primary-700 transition-colors">
              Sign Up
            </Link>
          </div>
        </header>

        {/* Form area */}
        <div className="flex-1 flex items-start justify-center pt-16 xl:pt-24 pb-10 px-5 sm:px-8 lg:px-12 xl:px-20">
          <div className="w-full max-w-[400px]">
            {/* Title */}
            <div className="mb-8">
              <h1 className="text-[28px] sm:text-[32px] font-bold text-gray-900 tracking-tight mb-2">Welcome back</h1>
              <p className="text-base text-gray-500">Enter your credentials to access your account</p>
            </div>

            {/* Social Login */}
            <div className="mb-6">
              <p className="text-base text-gray-600 mb-3">Sign in with</p>
              <div className="flex gap-3">
                <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                  <div className="flex-1 min-w-0">
                    <GoogleLogin
                      onSuccess={async (credentialResponse) => {
                        try {
                          setLoading(true);
                          const result = await googleLogin(credentialResponse.credential);
                          
                          if (result.success) {
                            navigate(result.redirectUrl || '/');
                          } else if (result.needsRegistration && result.email) {
                            navigate('/register', { 
                              state: { 
                                email: result.email,
                                name: result.name || '',
                                fromGoogle: true
                              }
                            });
                            toast.info('Please complete your registration');
                          }
                        } catch (err) {
                          setError('Failed to log in with Google. Please try again.');
                          console.error(err);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      onError={() => {
                        setError('Google login failed. Please try again.');
                      }}
                      theme="outline"
                      text="continue_with"
                      size="large"
                      width="100%"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <LinkedInLoginButton iconOnly />
                  </div>
                </GoogleOAuthProvider>
              </div>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-white text-sm text-gray-500 uppercase tracking-wider">or continue with email</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-base animate-shake">
                  {error}
                </div>
              )}
              
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-base font-medium text-gray-700 mb-1.5">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              
              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-base font-medium text-gray-700">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className="w-full h-12 px-4 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              {/* Remember Me */}
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="remember-me" className="ml-2 text-base text-gray-600">
                  Keep me signed in
                </label>
              </div>
              
              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 flex justify-center items-center rounded-xl text-base font-semibold text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {loading ? (
                  <>
                    <FiLoader className="animate-spin mr-2 h-4 w-4" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-base text-gray-500">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                Sign up
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-5 px-5 sm:px-8 lg:px-10 border-t border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <p className="text-sm text-gray-500">&copy; 2026 Resumemo. All rights reserved.</p>
            <div className="flex space-x-5">
              <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Login;
